import * as React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import Sider from 'antd/es/layout/Sider';
import {
  Layout,
  Menu,
  Button,
  Dropdown,
  Modal,
  Form,
  Input,
  Space,
  Typography,
  message,
} from 'antd';
import { Content, Header } from 'antd/es/layout/layout';
import {
  KeyOutlined,
  LogoutOutlined,
  UserOutlined,
  LockOutlined,
  DashboardOutlined,
  ShopOutlined,
  TeamOutlined,
  TagsOutlined,
  NotificationOutlined,
  CreditCardOutlined,
  StarOutlined,
  AppstoreOutlined,
  CheckCircleOutlined,
  QrcodeOutlined,
  CustomerServiceOutlined,
} from '@ant-design/icons';
import { useLogout, useUser } from 'lib/auth';
import { updateAdminPassword } from 'features/auth';
import storage from 'utils/storage';
import { useBrandToken } from 'components/PageShell';

const { Text } = Typography;

const menuItems = [
  {
    key: '/',
    icon: <DashboardOutlined />,
    label: 'Dashboard',
  },
  {
    key: 'merchants',
    icon: <ShopOutlined />,
    label: 'Merchants',
    children: [
      { key: '/merchant', label: 'All merchants', icon: <ShopOutlined /> },
      {
        key: '/merchantverification',
        label: 'Verification',
        icon: <CheckCircleOutlined />,
      },
      { key: '/outlets', label: 'Outlets', icon: <AppstoreOutlined /> },
    ],
  },
  {
    key: 'team',
    icon: <TeamOutlined />,
    label: 'Team',
    children: [
      { key: '/sales-reps', label: 'Sales reps', icon: <UserOutlined /> },
    ],
  },
  {
    key: '/qr-management',
    icon: <QrcodeOutlined />,
    label: 'QR Management',
  },
  {
    key: '/customers',
    icon: <TeamOutlined />,
    label: 'Customers',
  },
  {
    key: '/coupons',
    icon: <TagsOutlined />,
    label: 'Coupons',
  },
  {
    key: '/categorylist',
    icon: <AppstoreOutlined />,
    label: 'Categories',
  },
  {
    key: '/tickets',
    icon: <CustomerServiceOutlined />,
    label: 'Support tickets',
  },
  {
    key: '/feedback',
    icon: <StarOutlined />,
    label: 'Feedback',
  },
  {
    key: '/notification',
    icon: <NotificationOutlined />,
    label: 'Notifications',
  },
  {
    key: 'billing',
    icon: <CreditCardOutlined />,
    label: 'Billing',
    children: [
      { key: '/plan', label: 'Plans' },
      { key: '/subscription', label: 'Subscriptions' },
    ],
  },
];

export const MainLayout = ({ children } = {}) => {
  const [collapsed, setCollapsed] = useState(false);
  const [passwordOpen, setPasswordOpen] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const location = useLocation();
  const logout = useLogout();
  const { data: user } = useUser();
  const colors = useBrandToken();

  const selectedKeys = [location.pathname === '' ? '/' : location.pathname];
  const openKeys = menuItems
    .filter((i) => i.children?.some((c) => c.key === location.pathname))
    .map((i) => i.key);

  const openChangePassword = () => {
    form.resetFields();
    setPasswordOpen(true);
  };

  const handleChangePassword = async (values) => {
    setSavingPassword(true);
    try {
      const response = await updateAdminPassword({
        currentPassword: values.currentPassword,
        newPassword: values.newPassword,
        newPasswordConfirm: values.newPasswordConfirm,
      });
      if (response?.token) {
        storage.setToken(response.token);
      }
      message.success('Password updated successfully');
      setPasswordOpen(false);
      form.resetFields();
    } catch (error) {
      console.error('Change password failed:', error);
    } finally {
      setSavingPassword(false);
    }
  };

  const accountMenu = {
    items: [
      {
        key: 'change-password',
        icon: <KeyOutlined />,
        label: 'Change password',
        onClick: openChangePassword,
      },
      { type: 'divider' },
      {
        key: 'logout',
        icon: <LogoutOutlined />,
        label: 'Log out',
        danger: true,
        onClick: () => logout.mutate(),
      },
    ],
  };

  return (
    <Layout style={{ minHeight: '100vh', background: colors.background }}>
      <Sider
        collapsible
        collapsed={collapsed}
        onCollapse={setCollapsed}
        width={240}
        style={{ background: colors.primary }}
      >
        <div
          style={{
            height: 64,
            display: 'flex',
            alignItems: 'center',
            justifyContent: collapsed ? 'center' : 'flex-start',
            padding: collapsed ? 0 : '0 20px',
            color: colors.onPrimary,
            fontWeight: 800,
            fontSize: collapsed ? 16 : 20,
            letterSpacing: 0.3,
            borderBottom: '1px solid var(--color-on-primary-subtle)',
          }}
        >
          {collapsed ? 'SU' : 'ScanU Admin'}
        </div>
        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={selectedKeys}
          defaultOpenKeys={openKeys.length ? openKeys : ['merchants', 'billing']}
          items={menuItems}
          style={{ background: colors.primary, borderInlineEnd: 0 }}
          onClick={(val) => {
            if (val.key.startsWith('/')) navigate(val.key);
          }}
        />
      </Sider>
      <Layout>
        <Header
          style={{
            padding: '0 20px',
            background: colors.white,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: `1px solid ${colors.border}`,
          }}
        >
          <Text style={{ color: colors.muted, fontSize: 13 }}>
            Platform control for merchants, customers & offers
          </Text>
          <Dropdown menu={accountMenu} placement="bottomRight">
            <Button type="text">
              <Space>
                <UserOutlined style={{ color: colors.primary }} />
                <Text strong style={{ color: colors.primary }}>
                  {user?.full_name || user?.email || 'Admin'}
                </Text>
              </Space>
            </Button>
          </Dropdown>
        </Header>
        <Content style={{ margin: '0 16px', background: colors.background }}>
          {children}
        </Content>
      </Layout>

      <Modal
        title="Change password"
        open={passwordOpen}
        onCancel={() => {
          setPasswordOpen(false);
          form.resetFields();
        }}
        footer={null}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleChangePassword}
          style={{ marginTop: 8 }}
        >
          <Form.Item
            label="Current password"
            name="currentPassword"
            rules={[{ required: true, message: 'Enter current password' }]}
          >
            <Input.Password prefix={<LockOutlined />} />
          </Form.Item>
          <Form.Item
            label="New password"
            name="newPassword"
            rules={[
              { required: true, message: 'Enter new password' },
              { min: 8, message: 'At least 8 characters' },
            ]}
          >
            <Input.Password prefix={<LockOutlined />} />
          </Form.Item>
          <Form.Item
            label="Confirm new password"
            name="newPasswordConfirm"
            dependencies={['newPassword']}
            rules={[
              { required: true, message: 'Confirm new password' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('newPassword') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('Passwords do not match'));
                },
              }),
            ]}
          >
            <Input.Password prefix={<LockOutlined />} />
          </Form.Item>
          <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
            <Button onClick={() => setPasswordOpen(false)}>Cancel</Button>
            <Button type="primary" htmlType="submit" loading={savingPassword}>
              Update password
            </Button>
          </Space>
        </Form>
      </Modal>
    </Layout>
  );
};
